"use client";

import { useState, useEffect } from "react";

export default function AccommodationBookingsTable() {
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [error, setError] = useState(null);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);

  const ACCOMMODATIONS_URL =
    "https://eventscons.com/eventbackendnew/api/accommodation-bookings";

  const getAuthToken = () => {
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("access_token")
      );
    }
    return null;
  };

  // ------- LIST -------

  const extractList = (json) => {
    if (Array.isArray(json)) return json;
    if (Array.isArray(json.data)) return json.data;
    if (Array.isArray(json.accommodation_bookings))
      return json.accommodation_bookings;
    if (Array.isArray(json.bookings)) return json.bookings;
    if (Array.isArray(json.items)) return json.items;
    return [];
  };

  const fetchBookings = async () => {
    try {
      setLoadingBookings(true);
      setError(null);

      const token = getAuthToken();

      const res = await fetch(ACCOMMODATIONS_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok)
        throw new Error(`Failed to load accommodation bookings: ${res.status}`);

      const json = await res.json();
      console.log("ACCOMMODATIONS RESPONSE:", json);

      const list = extractList(json);
      setBookings(list);
    } catch (err) {
      console.error("Error fetching accommodation bookings:", err);
      setError(err.message);
      setBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // ------- DETAILS BY ID -------

  const extractDetails = (json) => {
    if (json?.booking && typeof json.booking === "object") return json.booking;
    if (json?.data && typeof json.data === "object") return json.data;
    return json;
  };

  const openDetailsModal = async (id) => {
    try {
      setDetailsLoading(true);
      setError(null);
      setDetailsData(null);

      const token = getAuthToken();

      const res = await fetch(`${ACCOMMODATIONS_URL}/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok)
        throw new Error(`Failed to load booking details: ${res.status}`);

      const json = await res.json();
      console.log("ACCOMMODATION DETAILS RESPONSE:", json);

      const data = extractDetails(json);
      setDetailsData(data);
      setDetailsModalOpen(true);
    } catch (err) {
      console.error("Error fetching booking details:", err);
      setError(err.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setDetailsData(null);
  };

  // ------- Styles -------

  const containerStyle = {
    padding: "24px",
    maxWidth: "1200px",
    width: "100%",
    margin: "0 auto",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
  };
  const cardStyle = {
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    overflow: "hidden",
  };
  const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
  const thStyle = {
    padding: "16px 20px",
    textAlign: "left",
    fontWeight: "600",
    color: "#64748b",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    backgroundColor: "#f1f5f9",
    borderBottom: "2px solid #e2e8f0",
  };
  const tdStyle = {
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
    verticalAlign: "middle",
  };
  const btnStyle = {
    padding: "6px 10px",
    background: "linear-gradient(135deg, #9B1321 0%, #9B1321 100%)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontWeight: "500",
    fontSize: "12px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginRight: "6px",
  };

  const modalOverlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };
  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "24px",
    width: "100%",
    maxWidth: "600px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
  };

  if (loadingBookings) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #9B1321",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: "20px",
            }}
          />
          <span style={{ fontSize: "18px", color: "#64748b" }}>
            Loading accommodation bookings...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        tr:hover {
          background-color: #f8fafc !important;
        }
        button:hover {
          transform: translateY(-1px) !important;
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "32px",
              fontWeight: "800",
              background:
                "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              margin: 0,
            }}
          >
            Accommodation Bookings ({bookings.length})
          </h2>
          <p
            style={{
              margin: "8px 0 0 0",
              color: "#64748b",
              fontSize: "14px",
            }}
          >
            List of all accommodation bookings from API.
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button onClick={fetchBookings} style={btnStyle}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Error inline */}
      {error && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px 16px",
            borderRadius: "8px",
            backgroundColor: "#fef2f2",
            color: "#b91c1c",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      )}

      {/* Table / empty */}
      {bookings.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "80px 40px",
            backgroundColor: "white",
            borderRadius: "16px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              fontSize: "64px",
              marginBottom: "20px",
              opacity: 0.3,
            }}
          >
            📭
          </div>
          <p
            style={{
              fontSize: "20px",
              color: "#64748b",
              marginBottom: "24px",
            }}
          >
            No accommodation bookings found.
          </p>
          <button onClick={fetchBookings} style={btnStyle}>
            Reload Data
          </button>
        </div>
      ) : (
        <div style={{ ...cardStyle, overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Guest Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Phone</th>
                <th style={thStyle}>Room Type</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id}>
                  <td style={tdStyle}>{b.id}</td>
                  <td style={tdStyle}>
                    {b.full_name || b.guest_name || "-"}
                  </td>
                  <td style={tdStyle}>{b.email || "-"}</td>
                  <td style={tdStyle}>{b.phone || "-"}</td>
                  <td style={tdStyle}>{b.room_type || "-"}</td>
                  <td style={tdStyle}>{b.payment_status || b.status || "-"}</td>
                  <td style={tdStyle}>
                    <button
                      style={{
                        ...btnStyle,
                        background:
                          "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                      }}
                      onClick={() => openDetailsModal(b.id)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Details Modal */}
      {detailsModalOpen && (
        <div style={modalOverlayStyle} onClick={closeDetailsModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h3
              style={{
                marginTop: 0,
                marginBottom: "12px",
                fontSize: "20px",
              }}
            >
              Booking Details {detailsData?.id ? `#${detailsData.id}` : ""}
            </h3>

            {detailsLoading && (
              <p style={{ fontSize: "14px", color: "#64748b" }}>
                Loading details...
              </p>
            )}

            {!detailsLoading && detailsData && (
              <div style={{ fontSize: "14px", color: "#1f2933" }}>
                <p>
                  <strong>Full Name:</strong>{" "}
                  {detailsData.full_name || "-"}
                </p>
                <p>
                  <strong>Email:</strong> {detailsData.email || "-"}
                </p>
                <p>
                  <strong>Phone:</strong> {detailsData.phone || "-"}
                </p>
                <p>
                  <strong>Country:</strong> {detailsData.country || "-"}
                </p>
                <p>
                  <strong>Conference ID:</strong>{" "}
                  <span style={{ color: "#9B1321", fontWeight: "bold" }}>
                    {detailsData.conference_id || "-"}
                  </span>
                </p>
                <p>
                  <strong>Check-in:</strong>{" "}
                  {detailsData.check_in
                    ? new Date(
                        detailsData.check_in
                      ).toLocaleDateString()
                    : "-"}
                </p>
                <p>
                  <strong>Check-out:</strong>{" "}
                  {detailsData.check_out
                    ? new Date(
                        detailsData.check_out
                      ).toLocaleDateString()
                    : "-"}
                </p>
                <p>
                  <strong>Guests:</strong>{" "}
                  {detailsData.guests != null ? detailsData.guests : "-"}
                </p>
                <p>
                  <strong>Room Type:</strong>{" "}
                  {detailsData.room_type || "-"}
                </p>
                <p>
                  <strong>Hotel Choice:</strong>{" "}
                  {detailsData.hotel_choice || "-"}
                </p>
                <p>
                  <strong>Bed Type:</strong>{" "}
                  {detailsData.bed_type || "-"}
                </p>
                <p>
                  <strong>Special Requirements:</strong>{" "}
                  {detailsData.special_requirements || "-"}
                </p>
                <p>
                  <strong>Amount:</strong>{" "}
                  {detailsData.amount ? `$${detailsData.amount}` : "-"}
                </p>
                <p>
                  <strong>PayPal Order ID:</strong>{" "}
                  {detailsData.paypal_order_id || "-"}
                </p>
                <p>
                  <strong>Payment Status:</strong>{" "}
                  {detailsData.payment_status || "-"}
                </p>
                <p>
                  <strong>Terms Accepted:</strong>{" "}
                  {detailsData.terms_accepted ? "Yes" : "No"}
                </p>
                <p>
                  <strong>Created At:</strong>{" "}
                  {detailsData.created_at
                    ? new Date(
                        detailsData.created_at
                      ).toLocaleString()
                    : "-"}
                </p>
                <p>
                  <strong>Updated At:</strong>{" "}
                  {detailsData.updated_at
                    ? new Date(
                        detailsData.updated_at
                      ).toLocaleString()
                    : "-"}
                </p>
              </div>
            )}

            {!detailsLoading && !detailsData && (
              <p style={{ fontSize: "14px", color: "#b91c1c" }}>
                Could not load details.
              </p>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "16px",
                gap: "8px",
              }}
            >
              <button
                style={{ ...btnStyle, background: "#6b7280" }}
                type="button"
                onClick={closeDetailsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
